import { useId, useState } from 'react';

import type { Business, WebsiteQuality } from '../../types';
import { formatDistance, googleSearchHref, shortenUrl, telHref } from '../../lib/format';
import { pagespeedOf } from '../../lib/assessment';
import type { BatchProgress } from '../App';
import { WEBSITE_QUALITY_LABELS, websiteLabel, websiteTone } from '../lib/labels';
import { IconChevron, IconGauge } from './Icons';

interface ToTreatFolderProps {
  businesses: readonly Business[];
  onSave: (business: Business) => void;
  onIgnore: (business: Business) => void;
  onQuality: (business: Business, quality: WebsiteQuality) => void;
  onAnalyze: (business: Business) => void;
  onAnalyzeAll: () => void;
  /** Arme le suivi post-appel : demande son issue au retour sur l'appli. */
  onCallStart: (business: Business) => void;
  analyzing: ReadonlySet<string>;
  batchProgress: BatchProgress | null;
  pendingPlaceId: string | null;
}

const VERDICTS: WebsiteQuality[] = ['BAD', 'GOOD'];

/**
 * Le dossier « À traiter », replié par défaut.
 *
 * Les entreprises qui ont déjà un site ne se prospectent pas comme les autres :
 * il faut d'abord ouvrir ce site et trancher. Elles sortent donc de la liste
 * principale sans disparaître.
 *
 * Le verdict rendu ici est humain et durable — aucune nouvelle recherche ne
 * l'écrase, et un mauvais score PageSpeed ne le rend pas à la place de
 * l'utilisateur. C'est pour ça que l'analyse technique est proposée à côté du
 * verdict et non à sa place : elle informe, elle ne tranche pas.
 */
export function ToTreatFolder(props: ToTreatFolderProps) {
  const [open, setOpen] = useState(false);
  const bodyId = useId();
  const { businesses } = props;

  if (businesses.length === 0) return null;

  const toAnalyze = businesses.filter((item) => pagespeedOf(item) === null).length;

  return (
    <section className="folder">
      <button
        type="button"
        className="folder-head"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="badge badge-opp-1">{businesses.length}</span>
        <span className="folder-title">
          À traiter
          <span className="folder-note">
            {businesses.length > 1 ? 'ont déjà un site' : 'a déjà un site'} — à regarder avant
            d’appeler
          </span>
        </span>
        <IconChevron className="folder-chevron" size={16} />
      </button>

      {open ? (
        <div className="folder-body" id={bodyId}>
          {props.batchProgress !== null ? (
            <p className="notice" role="status">
              Analyse en cours — {props.batchProgress.done} sur {props.batchProgress.total}.
            </p>
          ) : toAnalyze > 0 ? (
            <div className="folder-bar">
              <span className="cost" style={{ flex: 1, minWidth: '14ch' }}>
                L’analyse technique informe ton verdict, elle ne le remplace pas.
              </span>
              <button type="button" className="btn btn-soft btn-sm" onClick={props.onAnalyzeAll}>
                <IconGauge size={15} />
                Analyser les {toAnalyze} sites
              </button>
            </div>
          ) : null}

          <ul className="rows">
            {businesses.map((business) => {
              const pending = props.pendingPlaceId === business.placeId;
              const speed = pagespeedOf(business);
              const busy = props.analyzing.has(business.placeId);

              return (
                <li key={business.placeId} className="card-flat treat">
                  <div className="treat-head">
                    <div style={{ minWidth: 0 }}>
                      <a
                        className="row-name row-name-link"
                        href={googleSearchHref(business.name, business.address)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Rechercher sur Google"
                      >
                        {business.name}
                      </a>
                      <p className="row-meta" style={{ margin: '3px 0 0' }}>
                        <span>{formatDistance(business.distanceMeters)}</span>
                        {business.phone !== null ? (
                          <a
                            href={telHref(business.phone)}
                            onClick={() => props.onCallStart(business)}
                          >
                            {business.phone}
                          </a>
                        ) : (
                          <span className="badge badge-danger">Non joignable</span>
                        )}
                      </p>
                    </div>
                    <span
                      className={`badge ${websiteTone(business.websiteType, business.websiteQuality)}`}
                    >
                      {websiteLabel(business.websiteType, business.websiteQuality)}
                    </span>
                  </div>

                  <div className="row-facts" style={{ marginTop: 10 }}>
                    {business.website !== null ? (
                      <a href={business.website} target="_blank" rel="noreferrer noopener">
                        Ouvrir {shortenUrl(business.website)}
                      </a>
                    ) : null}
                    {speed !== null ? (
                      <span className="muted">
                        PageSpeed <strong>{speed.performance ?? '—'}</strong>
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="link"
                        onClick={() => props.onAnalyze(business)}
                        disabled={busy}
                      >
                        {busy ? 'Analyse…' : 'Analyser le site'}
                      </button>
                    )}
                  </div>

                  {/* Le verdict humain : deux choix, pas un menu déroulant. */}
                  <div className="verdicts-row">
                    <span className="eyebrow">Ton verdict</span>
                    <div className="verdicts">
                      {VERDICTS.map((quality) => (
                        <button
                          key={quality}
                          type="button"
                          className="chip"
                          aria-pressed={business.websiteQuality === quality}
                          disabled={pending}
                          onClick={() => props.onQuality(business, quality)}
                        >
                          {WEBSITE_QUALITY_LABELS[quality]}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="treat-actions">
                    <button
                      type="button"
                      className="btn btn-soft btn-sm"
                      onClick={() => props.onSave(business)}
                      disabled={pending}
                    >
                      {pending ? 'Envoi…' : 'Garder'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => props.onIgnore(business)}
                      disabled={pending}
                    >
                      Écarter
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
